import {
  computePlacement,
  type BackendCaps,
  type CaptureOptions,
  type Pose,
  type PoseFrame,
  type TrackingBackend,
} from "@visorxr/core";
import type { WebGLRenderer } from "three";
import type { EngineContext } from "../engine/engine-context";
import { isWebXRImmersiveArSupported } from "./support";
import { composite } from "../capture/compositor";
import { captureSize, DEFAULT_MAX_CAPTURE_EDGE } from "../capture/layout";
import { loadOptionalWatermark } from "../capture/load-image";
import { WebXRCapturePass } from "../capture/webxr-capture";

// caps describe only what this backend actually implements. hit-test / anchors are future work
// (the 8 m-rule anchoring seam in update()); advertising them now would mislead caps-driven UX.
const WEBXR_CAPS: BackendCaps = {
  sixDoF: true,
  hitTest: false,
  worldAnchors: false,
  rawCameraAccess: true,
};

const CAMERA_ACCESS = "camera-access";

interface PendingCapture {
  opts: CaptureOptions;
  resolve: (blob: Blob) => void;
  reject: (error: unknown) => void;
}

/**
 * Android 6DoF backend: an `immersive-ar` session (three.js drives the camera), with watermarked
 * capture via Raw Camera Access. If `camera-access` isn't granted, start() throws so the
 * TrackingManager degrades to the gyro path rather than shipping a broken capture (§5.5).
 */
export class WebXRBackend implements TrackingBackend<EngineContext> {
  readonly id = "webxr" as const;
  readonly caps = WEBXR_CAPS;

  private ctx: EngineContext | null = null;
  private session: XRSession | null = null;
  private objectPose: Pose | null = null;
  private pending: PendingCapture | null = null;
  private readonly capturePass = new WebXRCapturePass();

  isSupported(): Promise<boolean> {
    return isWebXRImmersiveArSupported(navigator);
  }

  async start(ctx: EngineContext): Promise<void> {
    // Placement is only optional on the type for marker-only descriptors (core/src/types.ts) —
    // this backend places a floor-elevated glTF, so it has nothing to place without one.
    if (!ctx.descriptor.placement) {
      throw new Error(
        "WebXRBackend requires descriptor.placement; a marker-only descriptor has none — only " +
          'list "webxr" in tracking.prefer alongside a placement (and a model to place).',
      );
    }

    this.ctx = ctx;
    this.objectPose = computePlacement(ctx.descriptor.placement);

    const session = await navigator.xr!.requestSession("immersive-ar", {
      requiredFeatures: ["local-floor"],
      // Only request what we use. hit-test/anchors return when caps advertise them.
      optionalFeatures: ["camera-access", "dom-overlay"],
      domOverlay: { root: ctx.overlayRoot },
    } as XRSessionInit);

    // Held before anything fallible runs against it. startFirst's fallback calls stop() on a failed
    // backend to release what it acquired, and stop() can only release what it can see — publishing
    // the session only after setSession resolves leaves an immersive session live, and the renderer
    // in XR mode, while the player quietly starts the gyro path underneath it.
    this.session = session;
    const renderer = ctx.scene.renderer;
    try {
      if (!session.enabledFeatures?.includes(CAMERA_ACCESS)) {
        throw new Error("WebXR did not grant camera-access; degrading to the gyro path");
      }
      renderer.xr.enabled = true;
      renderer.xr.setReferenceSpaceType("local-floor");
      await renderer.xr.setSession(session);
      // The user or the browser can end an immersive session without going through stop(). three
      // halts its XR animation loop on that event, so nothing would ever drain a queued capture —
      // it would hang forever — and this.session would keep claiming a session that is gone.
      session.addEventListener(
        "end",
        () => {
          this.session = null;
          renderer.xr.enabled = false;
          const pending = this.pending;
          this.pending = null;
          pending?.reject(new Error("the WebXR session ended before the capture could be taken"));
        },
        { once: true },
      );
    } catch (error) {
      renderer.xr.enabled = false;
      try {
        await session.end();
      } catch {
        // A session that refuses to end is already gone; the original failure is the useful one.
      }
      this.session = null;
      throw error;
    }
  }

  // three.js drives the camera from the XRFrame; we own only the object placement. A queued capture
  // is drained here because the RCA camera texture is valid only inside the live frame callback, and
  // it needs this frame's XRFrame to resolve the XRView.camera the texture is keyed by.
  update(_dt: number, frame?: unknown): PoseFrame {
    if (this.pending) {
      const pending = this.pending;
      this.pending = null;
      this.runCapture(pending.opts, frame as XRFrame | undefined).then(pending.resolve, pending.reject);
    }

    const camera = this.ctx!.scene.camera;
    return {
      camera: {
        position: { x: camera.position.x, y: camera.position.y, z: camera.position.z },
        orientation: {
          x: camera.quaternion.x,
          y: camera.quaternion.y,
          z: camera.quaternion.z,
          w: camera.quaternion.w,
        },
      },
      // start() already refused a descriptor with no placement, so this is the same guarantee
      // every other `this.ctx!` in this method already leans on.
      object: this.objectPose ?? computePlacement(this.ctx!.descriptor.placement!),
      trackingState: "tracking",
    };
  }

  capture(opts: CaptureOptions): Promise<Blob> {
    if (!this.session) return Promise.reject(new Error("WebXRBackend.capture called before start()"));
    // One capture at a time: a second request before the next frame drains the first would orphan
    // the first promise. The UI disables the shutter during capture, so this is a fail-fast guard.
    if (this.pending) return Promise.reject(new Error("a WebXR capture is already in progress"));

    return new Promise<Blob>((resolve, reject) => {
      this.pending = { opts, resolve, reject };
    });
  }

  private async runCapture(opts: CaptureOptions, frame?: XRFrame): Promise<Blob> {
    const renderer = this.ctx!.scene.renderer;

    // three keys its RCA texture map by the live XRView.camera (an XRCamera), NOT by
    // renderer.xr.getCamera() — despite getCameraTexture's misleading three-camera arg type. Passing
    // the three camera silently misses (undefined), so re-read this frame's XRView.camera and pass that.
    // Fall back to the XR manager's current frame if the loop didn't thread one through.
    const xrFrame = frame ?? renderer.xr.getFrame();
    const viewCamera = readXrViewCamera(renderer, xrFrame);
    if (!viewCamera) {
      throw new Error(describeMissingRcaCamera(renderer, xrFrame));
    }
    type CameraTextureArg = Parameters<typeof renderer.xr.getCameraTexture>[0];
    const cameraTexture = renderer.xr.getCameraTexture(viewCamera as unknown as CameraTextureArg);
    // getCameraTexture returns the wrapper even when the browser supplied no image this frame; an
    // empty sourceTexture would silently render black, so surface that as its own cause.
    const source = cameraTexture?.sourceTexture as WebGLTexture | null | undefined;
    if (!source) {
      throw new Error(
        "Raw Camera Access returned no image this frame (sourceTexture=null) — camera-access is " +
          "granted but the device provided no camera texture",
      );
    }

    const { width, height } =
      opts.width && opts.height
        ? { width: opts.width, height: opts.height }
        : captureSize(renderer.domElement.width, renderer.domElement.height, DEFAULT_MAX_CAPTURE_EDGE);

    // Read the camera feed straight off its GL texture at native resolution (XRCamera dimensions).
    const cam = viewCamera as { width?: number; height?: number };
    const camWidth = cam.width && cam.width > 0 ? cam.width : renderer.domElement.width;
    const camHeight = cam.height && cam.height > 0 ? cam.height : renderer.domElement.height;
    const camera = this.capturePass.cameraTextureToCanvas(renderer, source, camWidth, camHeight);
    if (camera.maxChannel < 8) {
      // Direct read failed too. The status distinguishes the remaining causes: an incomplete
      // framebuffer means the texture isn't a plain TEXTURE_2D (external/OES → needs a
      // samplerExternalOES pass); a complete framebuffer that still reads zero means the texture is
      // protected on this device (→ getUserMedia fallback).
      throw new Error(
        `Raw Camera Access image unreadable (direct readback maxChannel=${camera.maxChannel}, ` +
          `fbStatus=0x${camera.status.toString(16)}, ${camWidth}x${camHeight}) — the camera texture ` +
          "cannot be read on this device",
      );
    }

    // Render the scene from three's XR eye camera (the texture only supplied the camera-feed key).
    const sceneCamera = renderer.xr.getCamera().cameras[0];
    const sceneCanvas = this.capturePass.sceneToCanvas(renderer, this.ctx!.scene.scene, sceneCamera, width, height);
    const watermark = await loadOptionalWatermark(opts.watermarkUrl);

    return composite(
      { camera: camera.canvas, cameraWidth: camWidth, cameraHeight: camHeight, scene: sceneCanvas, watermark },
      { width, height, mimeType: opts.mimeType, quality: opts.quality },
    );
  }

  stop(): void {
    // A queued capture is drained only by update(); if we stop first, settle it so callers awaiting
    // capture() don't hang forever (which would leave the capture button permanently disabled).
    this.pending?.reject(new Error("capture cancelled: WebXR session stopped"));
    this.pending = null;
    if (this.ctx) this.ctx.scene.renderer.xr.enabled = false;
    void this.session?.end();
    this.session = null;
  }
}

type XRViewWithCamera = XRView & { camera?: object };

/** This frame's XRView.camera (the raw-camera-access XRCamera), scanning every view. */
function readXrViewCamera(renderer: WebGLRenderer, frame?: XRFrame): object | undefined {
  if (!frame) return undefined;
  const refSpace = renderer.xr.getReferenceSpace();
  if (!refSpace) return undefined;
  const pose = frame.getViewerPose(refSpace);
  if (!pose) return undefined;
  for (const view of pose.views) {
    const camera = (view as XRViewWithCamera).camera;
    if (camera) return camera;
  }
  return undefined;
}

/**
 * When the RCA camera can't be found, report the actual runtime state — the failure is one of
 * several (no frame, no pose, or camera-access enabled but the device doesn't expose XRView.camera)
 * and the specifics decide the fix. Surfaced in the capture-error panel for on-device diagnosis.
 */
function describeMissingRcaCamera(renderer: WebGLRenderer, frame?: XRFrame): string {
  const refSpace = renderer.xr.getReferenceSpace();
  const pose = frame && refSpace ? frame.getViewerPose(refSpace) : undefined;
  const views = pose?.views ?? [];
  const viewInfo = views
    .map((v, i) => `v${i}:{in=${"camera" in (v as object)},t=${typeof (v as XRViewWithCamera).camera}}`)
    .join(" ");
  const features = renderer.xr.getSession()?.enabledFeatures;
  const feats = features ? Array.from(features).join(",") : "n/a";
  return `RCA camera missing — frame=${!!frame} refSpace=${!!refSpace} pose=${!!pose} views=${views.length} [${viewInfo}] feats=[${feats}]`;
}
